import { uniqueId } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import SnackBar, { type SnackBarProps } from '../Components/SnackBar';
import type { SnackBarOptions } from '../types/app';

type SnackBarHelpers = {
  addSnackBar: (props: SnackBarOptions) => void;
};

export const SnackBarContext = createContext<SnackBarHelpers>({
  addSnackBar: () => {},
});

export const useSnackBarContext = () => useContext(SnackBarContext);

export default function SnackBarProvider({ children }: { children: ReactNode }) {
  const [snackBars, setSnackBars] = useState<ReactElement<SnackBarProps>[]>([]);

  const removeSnackBar = useCallback((snackBarId: string) => {
    setSnackBars((current) => current.filter((sb) => sb.props.id !== snackBarId));
  }, []);

  const addSnackBar = useCallback(
    (snackBarProps: SnackBarOptions) => {
      const id = uniqueId();
      setSnackBars((current) => [
        ...current,
        <SnackBar
          key={id}
          id={id}
          onClose={() => removeSnackBar(id)}
          {...snackBarProps}
        />,
      ]);
    },
    [removeSnackBar]
  );

  return (
    <SnackBarContext.Provider value={{ addSnackBar }}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-20 z-[100] flex flex-col items-center gap-2 px-4">
        <div className="pointer-events-auto flex w-full max-w-md flex-col gap-2">
          {snackBars}
        </div>
      </div>
    </SnackBarContext.Provider>
  );
}
